import { useEffect, useState } from 'react';
import {
  DASHBOARD_FALLBACK,
  type DashboardBucket,
  type DashboardStatKind,
  type StarlinkDashboardPayload,
} from '@/components/value-chain/starlinkDashboard';
import { valueChainService } from '@/services/api';

const KIND_CLASS: Record<DashboardStatKind, string> = {
  printed: 'bg-slate-900 text-white',
  independent: 'bg-sky-100 text-sky-800',
  company_report: 'bg-amber-100 text-amber-900',
  hole: 'bg-rose-100 text-rose-800',
};

export function useStarlinkDashboard(): StarlinkDashboardPayload {
  const [payload, setPayload] = useState<StarlinkDashboardPayload>(DASHBOARD_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    valueChainService
      .getStarlinkDashboard()
      .then((next) => {
        if (!cancelled && next?.buckets?.length) setPayload(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return payload;
}

export function StarlinkIndependentSeries() {
  const payload = useStarlinkDashboard();
  if (!payload.buckets.length) return null;
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Independent series</h2>
          <p className="mt-0.5 max-w-3xl text-[12px] leading-relaxed text-slate-500">{payload.caveat}</p>
        </div>
        <p className="text-[11px] text-slate-400">
          {payload.ok_count}/{payload.buckets.length} buckets · {payload.live_count} live · {payload.as_of}
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-2">
        {payload.buckets.map((bucket) => (
          <BucketCard key={bucket.id} bucket={bucket} />
        ))}
      </div>
    </section>
  );
}

function BucketCard({ bucket }: { bucket: DashboardBucket }) {
  return (
    <article className="flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-[14px] font-semibold text-slate-900">{bucket.title}</h3>
          <p className="mt-0.5 text-[12px] leading-relaxed text-slate-500">{bucket.why}</p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
            bucket.live ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {bucket.live ? 'Live' : 'File'}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-2">
        {bucket.stats.map((stat) => (
          <div key={stat.label} className="rounded-xl bg-slate-50 px-3 py-2">
            <dt className="flex items-center justify-between gap-1 text-[10px] text-slate-500">
              <span>{stat.label}</span>
              <span className={`rounded-full px-1.5 py-0.5 ${KIND_CLASS[stat.kind] ?? 'bg-slate-100 text-slate-500'}`}>
                {kindShort(stat.kind)}
              </span>
            </dt>
            <dd className="mt-1 text-[16px] leading-tight font-semibold tracking-tight text-slate-900">
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>
      {bucket.rows.length ? (
        <ul className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
          {bucket.rows.slice(0, 5).map((row) => (
            <li key={`${row.label}-${row.value}`} className="flex justify-between gap-3 text-[12px]">
              <span className="min-w-0 truncate text-slate-600">{row.label}</span>
              <span className="shrink-0 font-medium text-slate-900">{row.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="mt-auto pt-3 text-[10px] leading-relaxed text-slate-400">
        {bucket.note} {bucket.source}
      </p>
    </article>
  );
}

function kindShort(kind: DashboardStatKind): string {
  if (kind === 'printed') return '10-Q';
  if (kind === 'independent') return 'Feed';
  if (kind === 'company_report') return 'Co.';
  return 'Hole';
}
